import { StoreType } from '@unity/commerce-bridge';
import { getSafeLocale } from 'utils';

// Mirrors CHECKOUT_URLS from @unity/commerce-ui
// (packages/ui/src/core/constants.ts) which is not exported from @unity/commerce-core
const CHECKOUT_URLS = {
  DEVELOPMENT: 'https://checkout.local.unity.com',
  TEST: 'https://checkout-test.unity.com',
  STAGING: 'https://checkout-staging.unity.com',
  PRODUCTION: 'https://checkout.unity.com',
} as const;

export type BuildCheckoutUrlArgs = {
  env: string | undefined;
  locale: string;
  // Required: checkout cannot resolve a cart without it. Callers must guard
  // against a missing org before building the URL.
  organizationId: string;
  // Narrow union from useCartManagement().store so a stray store value can't
  // reach the checkout URL.
  store: StoreType;
};

export const buildCheckoutUrl = ({
  env,
  locale,
  organizationId,
  store,
}: BuildCheckoutUrlArgs): string => {
  const envKey = (env ?? 'staging')
    .trim()
    .toUpperCase() as keyof typeof CHECKOUT_URLS;
  const baseUrl = CHECKOUT_URLS[envKey] ?? CHECKOUT_URLS.STAGING;
  // Checkout serves all routes under a required `[lang]` segment and maps each
  // BCP47 locale to its short form. getSafeLocale keeps us to our supported set.
  const lang = getSafeLocale(locale);
  // Checkout resolves the active cart from organizationId + store, so we point
  // at the org's cart route rather than a specific cartId.
  const checkoutUrl = new URL(`${baseUrl}/${lang}/cart`);
  checkoutUrl.searchParams.append('organizationId', organizationId);
  checkoutUrl.searchParams.append('store', store);
  return checkoutUrl.toString();
};
